import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import authService from '../../common/services/authService';
import {handleError} from '../../common/utils/errorHandling';
import {CenteredButton, CenteredHeader, CenteredInfo, CenteredLayout} from '../../layout/centered/components';
import MessageBox from '../components/MessageBox';

const EmailConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const confirmEmail = async () => {
      const id = searchParams.get('id');
      const token = searchParams.get('token');

      if (!id || !token) {
        setStatus('error');
        setErrorMessage('Invalid confirmation link. Missing required parameters.');
        return;
      }

      await handleError(
        async () => {
          await authService.confirmEmail(id, token);
          setStatus('success');
          setTimeout(() => {
            navigate('/login');
          }, 4000);
        },
        'Email confirmation failed:',
        () => {
          setStatus('error');
          setErrorMessage('Email confirmation failed. The link may be invalid or expired.');
        }
      );
    };

    confirmEmail();
  }, [searchParams, navigate]);

  return (
    <CenteredLayout sectionId="email-confirmation-section">
      {status === 'loading' && (
        <>
          <CenteredHeader
            title="Confirming Email"
            subtitle="Please wait while we confirm your email address"
          />
          <MessageBox
            icon="fa fa-spinner fa-spin"
            message="Processing your email confirmation..."
          />
        </>
      )}

      {status === 'success' && (
        <>
          <CenteredHeader
            title="Email Confirmed!"
            subtitle="Your email has been successfully confirmed"
          />
          <MessageBox
            icon="fa fa-check-circle"
            message="You will be redirected to the login page shortly."
          />
          <CenteredInfo className="mt-4">
            Redirecting in 2 seconds...
          </CenteredInfo>
        </>
      )}

      {status === 'error' && (
        <>
          <CenteredHeader
            title="Confirmation Failed"
            subtitle="We couldn't confirm your email address"
          />
          <MessageBox
            icon="fa fa-times-circle"
            message={errorMessage}
          />
          <div className="mt-8 text-center">
            <CenteredButton
              type="button"
              label="Go to Login"
              icon="fa fa-sign-in-alt"
              iconPos="left"
              onClick={() => navigate('/login')}
            />
          </div>
          <CenteredInfo className="mt-4">
            Need help?&nbsp;
            <Link
              className="text-gray-800 hover:underline cursor-pointer font-medium"
              to="/about"
            >
              Contact support
            </Link>
          </CenteredInfo>
        </>
      )}
    </CenteredLayout>
  );
};

export default EmailConfirmation;

